import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const PROCESS = [
  { n: "01", title: "Tell Us What You Need", text: "Share your quantity, budget, occasion and requirements." },
  { n: "02", title: "We Curate", text: "Our team creates a selection of suitable gifting options." },
  { n: "03", title: "Approve", text: "Review products, packaging and branding options." },
  { n: "04", title: "We Deliver", text: "We coordinate the order and fulfilment." },
];

export default function CorporateGifting() {
  const [taxonomy, setTaxonomy] = useState({ collections: [] });
  const [seasonal, setSeasonal] = useState([]);

  useEffect(() => {
    document.title = "Corporate Gifting — THE GOOD GRID";
    api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    api.get("/products", { params: { seasonal: true } }).then((r) => setSeasonal(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="corporate-gifting-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>Corporate Gifting</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl mt-4 tracking-tight max-w-4xl leading-[1.05]">
            Corporate gifting, <span className="italic text-champagne-light">without the guesswork.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/50 mt-6 max-w-xl leading-relaxed">
            One team for product selection, branding, packaging and fulfilment — built around your budget, your recipients and your timeline.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/request-quote" data-testid="cg-start-order" className="group inline-flex items-center gap-2 mt-10 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:bg-champagne-light transition-colors duration-300">
            Start Your Corporate Order <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 mt-24">
        <Reveal><Eyebrow>The Process</Eyebrow></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10 mt-8">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1} className="border-r border-b border-white/10 p-8 lg:p-10 hover:bg-[#161616] transition-colors duration-500">
              <p className="font-mono text-champagne text-sm tracking-[0.2em] mb-8">{p.n}</p>
              <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
              <p className="text-sm text-ivory/45 leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 mt-24">
        <Reveal><Eyebrow>Occasions</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-3xl sm:text-5xl mt-4 mb-12 tracking-tight">Built for every corporate moment.</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {taxonomy.collections.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.08}>
              <Link
                to={c.slug === "custom-corporate-gifting" ? "/custom-gifting" : `/collections?occasion=${encodeURIComponent(c.title)}`}
                data-testid={`cg-collection-${c.slug}`}
                className="group relative block overflow-hidden aspect-[4/5] border border-white/10"
              >
                <img src={c.image} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-[1.05] transition-all duration-[1.3s] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C] via-transparent to-transparent" />
                <div className="absolute bottom-0 p-7">
                  <p className="font-mono text-xs text-champagne tracking-[0.25em] mb-2">{c.number}</p>
                  <h3 className="font-serif text-2xl">{c.title}</h3>
                  <p className="text-xs text-ivory/50 mt-2 max-w-xs">{c.description}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>

      <div id="festive" className="px-4 sm:px-8 lg:px-16 mt-24">
        <div className="border border-champagne/30 bg-[#151210] p-8 lg:p-14">
          <Reveal><Eyebrow>Seasonal Programmes</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-5xl mt-4 tracking-tight">Festive corporate gifting.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-ivory/50 mt-4 max-w-2xl text-sm leading-relaxed">
              Diwali, New Year, employee appreciation and client appreciation programmes — planned early, delivered on time. Seasonal collections turn on and off through the year.
            </p>
          </Reveal>
          {seasonal.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 mt-12">
              {seasonal.slice(0, 3).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <ProductCard product={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
          <Reveal delay={0.2} className="mt-12">
            <Link to="/request-quote" data-testid="cg-festive-quote" className="group inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-champagne">
              Plan this season's gifting <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
